/**
 * Centralized bilingual text extraction utility.
 * Safely extracts a renderable string from fields that may be:
 *   - A plain string: "Hello"
 *   - A bilingual object: { en: "Hello", ms: "Helo", bm: "Helo" }
 *   - null / undefined
 *   - An unexpected type (number, array, etc.)
 *
 * Usage:
 *   import { txt } from '../utils/bilingualText';
 *   <span>{txt(pack.name)}</span>
 *   <span>{txt(pack.description, 'ms', 'Tiada Penerangan')}</span>
 */

const LANG_KEYS = ['en', 'ms', 'bm', 'urban'];

/**
 * @param {*} field       - The value to extract text from
 * @param {string} [lang] - Preferred language key (default: 'en')
 * @param {string} [fallback] - Fallback if no text found (default: '')
 * @returns {string} A safe, renderable string
 */
export function txt(field, lang = 'en', fallback = '') {
  if (field == null) return fallback;
  if (typeof field === 'string') return field || fallback;
  if (typeof field === 'number') return String(field);

  if (typeof field === 'object' && !Array.isArray(field)) {
    // Try preferred language first, then cycle through known keys
    if (field[lang]) return field[lang];
    for (const key of LANG_KEYS) {
      if (field[key]) return field[key];
    }
    // Last resort: grab any string value from the object
    for (const val of Object.values(field)) {
      if (typeof val === 'string' && val) return val;
    }
    return fallback;
  }

  // Arrays or other types — not renderable
  return fallback;
}

export default txt;
